import { useMemo } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  QueryClient as _QueryClient,
  DefaultError,
  MutationFunction,
  QueryCache,
  QueryFunction,
  MutationKey as ReactMutationKey,
  QueryClientProvider as ReactQueryClientProvider,
  QueryKey as ReactQueryKey,
  UseMutationOptions as ReactUseMutationOptions,
  UseQueryOptions as ReactUseQueryOptions,
  useMutation as useReactMutation,
  useQuery as useReactQuery,
} from '@tanstack/react-query'

import { TypedDocumentNode } from '@redwoodjs/web'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import { monitoring } from 'src/utils/monitoring'

export enum QueryKey {
  ActionProposals = 'ActionProposals',
  ActionProposalsCounts = 'ActionProposalsCounts',
  ActionTemplates = 'ActionTemplates',
  AutoCheckEnableForApplicantsContext = 'AutoCheckEnableForApplicantsContext',
  AutoCheckEnablementContext = 'AutoCheckEnablementContext',
  AutoCheckExecution = 'AutoCheckExecution',
  AutoCheckFeedbackItemMetrics = 'AutoCheckFeedbackItemMetrics',
  AutoCheckFeedbackItemsForRuleType = 'AutoCheckFeedbackItemsForRuleType',
  AutoCheckRuleTypeSummaries = 'AutoCheckRuleTypeSummaries',
  RecordAutoCheckFeedbackItems = 'RecordAutoCheckFeedbackItems',
  AdminUsersPage = 'AdminUsersPage',
  AIChatROIData = 'AIChatROIData',
  AIMostUsedChatTopics = 'AIMostUsedChatTopics',
  ActiveStaffMemberships = 'ActiveStaffMemberships',
  AIChatSession = 'AIChatSession',
  AIChatSessionOverview = 'AIChatSessionOverview',
  AICommunityAssistantUsage = 'AICommunityAssistantUsage',
  AIChatTopQuestions = 'AIChatTopQuestions',
  AICAValueMetrics = 'AICAValueMetrics',
  AIConfigChatSession = 'AIConfigChatSession',
  AIStaffMembers = 'AIStaffMembers',
  StaffAIChatSession = 'StaffAIChatSession',
  StaffAIChatSessions = 'StaffAIChatSessions',
  StaffAIChatSuggestedPrompts = 'StaffAIChatSuggestedPrompts',
  AlertsForRecord = 'AlertsForRecord',
  AppState = 'AppState',
  AutomationTemplates = 'AutomationTemplates',
  CalculateFeeAmount = 'CalculateFeeAmount',
  CalculateSampleLateFees = 'CalculateSampleLateFees',
  CalendarEvents = 'CalendarEvents',
  CalendarEventsById = 'CalendarEventsById',
  CandidateCustomColumns = 'CandidateCustomColumns',
  ChecklistTemplates = 'ChecklistTemplates',
  CitizenAddressesAndParcelsSearch = 'CitizenAddressesAndParcelsSearch',
  CitizenRecordTaskPayments = 'CitizenRecordTaskPayments',
  CitizenRecordTaskPaymentsStatusCounts = 'CitizenRecordTaskPaymentsStatusCounts',
  ClaimActivity = 'ClaimActivity',
  ClaimMigrationStats = 'ClaimMigrationStats',
  ClaimRecentActivity = 'ClaimRecentActivity',
  CodeBookItems = 'CodeBookItems',
  CodeBooks = 'CodeBooks',
  CodeBooksForCards = 'CodeBooksForCards',
  CommentTemplates = 'CommentTemplates',
  CompaniesByUser = 'CompaniesByUser',
  Company = 'Company',
  DateTemplate = 'DateTemplate',
  DocumentTemplates = 'DocumentTemplates',
  EditRecordDraftForm = 'EditRecordDraftForm',
  EmailTemplate = 'EmailTemplate',
  EmailTemplates = 'EmailTemplates',
  Enumeration = 'Enumeration',
  Enumerations = 'Enumerations',
  ExternalCalendarEvents = 'ExternalCalendarEvents',
  ExternalCalendarStatus = 'ExternalCalendarStatus',
  ExternalCalendarUnsyncedInspectionCount = 'ExternalCalendarUnsyncedInspectionCount',
  Fee = 'Fee',
  Fees = 'Fees',
  FeeSchedule = 'FeeSchedule',
  FeeSchedules = 'FeeSchedules',
  FeeTag = 'FeeTag',
  FeeTemplateForConfig = 'FeeTemplateForConfig',
  FeeTemplatesPage = 'FeeTemplatesPage',
  FieldGroup = 'FieldGroup',
  FieldGroupOnRecordSettings = 'FieldGroupOnRecordSettings',
  FieldGroupOnRecordSettingsCountByIsArchived = 'FieldGroupOnRecordSettingsCountByIsArchived',
  FieldGroupTemplate = 'FieldGroupTemplate',
  FieldTemplates = 'FieldTemplates',
  FieldTemplatesWhichCouldBeAutomationInput = 'FieldTemplatesWhichCouldBeAutomationInput',
  File = 'File',
  FilePageImageUrls = 'FileImageUrls',
  FilePageScrollTarget = 'FilePageScrollTarget',
  FilePageThumbnailUrl = 'FilePageThumbnailUrl',
  FileProcessed = 'FileProcessed',
  Files = 'Files',
  Formulas = 'Formulas',
  GenerateDocumentsPage = 'GenerateDocumentsPage',
  GetActiveUsersCount = 'GetActiveUsersCount',
  GetAddressRecordsAndViolations = 'GetAddressRecordsAndViolations',
  GetAIChatbotFAQWithSessions = 'GetAIChatbotFAQWithSessions',
  GetAIChatFallbackContacts = 'GetAIChatFallbackContacts',
  GetAllAITrainingMaterials = 'GetAllAITrainingMaterials',
  GetCommunitySessionsCount = 'GetCommunitySessionsCount',
  GetCronofyAuthUrl = 'GetCronofyAuthUrl',
  GetDataForLogMeetingResultForRecord = 'GetDataForLogMeetingResultForRecord',
  GetEntityDetails = 'GetEntityDetails',
  GetEntityLayers = 'GetEntityLayers',
  GetEntityVersionById = 'GetEntityVersionById',
  GetEntityVersionByLocationCounts = 'GetEntityVersionByLocationCounts',
  GetEntityVersionsById = 'GetEntityVersionsById',
  GetFeeCalculationExplanation = 'GetFeeCalculationExplanation',
  GetFileOnRecordV2 = 'GetFileOnRecordV2',
  GetGeneralLedgerNumbers = 'GetGeneralLedgerNumbers',
  GetIntercomJwt = 'GetIntercomJwt',
  GetInvoicesForSettlement = 'GetInvoicesForSettlement',
  GetOrganizationCalendarConnections = 'GetOrganizationCalendarConnections',
  GetOrganizationChecklistTemplatesSelect = 'GetOrganizationChecklistTemplatesSelect',
  GetOrganizationGovWellStaffManagement = 'GetOrganizationGovWellStaffManagement',
  GetOrganizationsGovWellStaffManagement = 'GetOrganizationsGovWellStaffManagement',
  GetParcelRecordsAndViolations = 'GetParcelRecordsAndViolations',
  GetReceiptsForSettlement = 'GetReceiptsForSettlement',
  GetRecordPlansSetReviewResultsForModuleForConfig = 'GetRecordPlansSetReviewResultsForModuleForConfig',
  GetSearchEntityVersionById = 'GetSearchEntityVersionById',
  GetSettlement = 'GetSettlement',
  GetSettlementByUuid = 'GetSettlementByUuid',
  GetTestApplicant = 'GetTestApplicant',
  GetUnansweredAIChatbotFAQsCount = 'GetUnansweredAIChatbotFAQsCount',
  GisAttributes = 'GisAttributes',
  ImageFilesOnRecordAndAncestors = 'ImageFilesOnRecordAndAncestors',
  InspectionAvailabilityWindows = 'InspectionAvailabilityWindows',
  InspectionAvailability = 'InspectionAvailability',
  InspectionResults = 'InspectionResults',
  InspectionResultsForRecordType = 'InspectionResultsForRecordType',
  InspectionTemplates = 'InspectionTemplates',
  IsRenewalActive = 'IsRenewalActive',
  Letterhead = 'Letterhead',
  Letterheads = 'Letterheads',
  LicenseClaimingData = 'LicenseClaimingData',
  LocationSnapshot = 'LocationSnapshot',
  EditInspectionAttemptModal = 'EditInspectionAttemptModal',
  LogInspectionModal = 'LogInspectionModal',
  Meeting = 'Meeting',
  MeetingBoard = 'MeetingBoard',
  MeetingBoards = 'MeetingBoards',
  MeetingHistoryItems = 'MeetingHistoryItems',
  MostRecentPlansSet = 'MostRecentPlansSet',
  MyApplicantRenewableRecords = 'MyApplicantRenewableRecords',
  MyCompanies = 'MyCompanies',
  MySignature = 'MySignature',
  NotificationSettings = 'NotificationSettings',
  OrganizationAISettings = 'OrganizationAISettings',
  OrganizationByRecordTemplateUuid = 'OrganizationByRecordTemplateUuid',
  OrganizationByRecordTypeId = 'OrganizationByRecordTypeId',
  OrganizationByRecordUuid = 'OrganizationByRecordUuid',
  OrganizationBySlug = 'OrganizationBySlug',
  OrganizationEntityLayers = 'OrganizationEntityLayers',
  OrganizationForAddressesAndParcelsPage = 'OrganizationForAddressesAndParcelsPage',
  OrganizationForAIChatWindow = 'OrganizationForAIChatWindow',
  OrganizationForInsightsInspections = 'OrganizationForInsightsInspections',
  OrganizationGisSetup = 'OrganizationGisSetup',
  OrganizationHoliday = 'OrganizationHoliday',
  OrganizationHolidayForDay = 'OrganizationHolidayForDay',
  OrganizationHolidays = 'OrganizationHolidays',
  OrganizationHolidaysWithinDateRange = 'OrganizationHolidaysWithinDateRange',
  OrganizationInspectionInformation = 'OrganizationInspectionInformation',
  OrganizationPaymentProviderFormatted = 'OrganizationPaymentProviderFormatted',
  OrganizationPublishedViewDashboards = 'OrganizationPublishedViewDashboards',
  OrganizationRecordSettings = 'OrganizationRecordSettings',
  OrganizationSlugAvailable = 'OrganizationSlugAvailable',
  PaginateEntities = 'PaginateEntities',
  PlanReviewModal = 'PlanReviewModal',
  PlanReviewModalReviewResults = 'PlanReviewModalReviewResults',
  PlanReviewStamps = 'PlanReviewStamps',
  Record = 'Record',
  RecordActivityTab = 'RecordActivityTab',
  RecordApplicationFields = 'RecordApplicationFields',
  RecordByUuid = 'RecordByUuid',
  RecordCaseReport = 'RecordCaseReport',
  RecordEmailPreviewData = 'RecordEmailPreviewData',
  RecordEntitiesForConditionAttachment = 'RecordEntitiesForConditionAttachment',
  RecordExpirationHistories = 'RecordExpirationHistories',
  RecordFeesTab = 'RecordFeesTab',
  RecordFieldGroupVersions = 'RecordFieldGroupVersions',
  RecordFilesForPlanReview = 'RecordFilesForPlanReview',
  RecordFilesList = 'RecordFilesList',
  RecordHistoryItemEmailModal = 'RecordHistoryItemEmailModal',
  RecordId = 'RecordId',
  RecordIndividualReport = 'RecordIndividualReport',
  RecordIssuedIdentifier = 'RecordIssuedIdentifier',
  RecordInPlaceRenewals = 'recordInPlaceRenewals',
  RecordInspectionsTab = 'RecordInspectionsTab',
  RecordOnViewDashboardView = 'RecordOnViewDashboardView',
  RecordMostRecentPlanSetReviewComments = 'RecordMostRecentPlanSetReviewComments',
  RecordPlansSet = 'RecordPlansSet',
  RecordPlansSetFilePages = 'RecordPlansSetFilePages',
  RecordPlansSetContextForFilePage = 'RecordPlansSetContextForFilePage',
  RecordPlansSetReviewResults = 'RecordPlansSetReviewResults',
  RecordPlansSets = 'RecordPlansSets',
  RecordPropertyTab = 'RecordPropertyTab',
  Records = 'Records',
  RecordsByCompany = 'RecordsByCompany',
  RecordsByUser = 'RecordsByUser',
  RecordSettingsForModule = 'RecordSettingsForModule',
  RecordShouldSuggestCreatingPlansSet = 'RecordShouldSuggestCreatingPlansSet',
  RecordsSearch = 'RecordsSearch',
  RecordTaskGenerateDocumentsPage = 'RecordTaskGenerateDocuments',
  RecordTaskGroupForPlanReview = 'RecordTaskGroupForPlanReview',
  RecordTaskInspectionAttemptReportFile = 'RecordTaskInspectionAttemptReportFile',
  RecordTaskInspectionAttemptLogEntryReportFile = 'RecordTaskInspectionAttemptLogEntryReportFile',
  RecordTaskInspectionAttemptIntakeForm = 'RecordTaskInspectionAttemptIntakeForm',
  RecordTaskInspectionAttemptsForCalendar = 'RecordTaskInspectionAttemptsForCalendar',
  RecordTaskReassignmentInfo = 'RecordTaskReassignmentInfo',
  RecordTaskTemplateGroupOptionsForRecordTemplate = 'RecordTaskTemplateGroupOptionsForRecordTemplate',
  RecordTaskTemplateGroupOptionsForWorkflowTemplate = 'RecordTaskTemplateGroupOptionsForWorkflowTemplate',
  RecordTaskTemplateGroupRouting = 'RecordTaskTemplateGroupRouting',
  RecordTemplate = 'RecordTemplate',
  RecordTemplateFees = 'RecordTemplateFees',
  RecordTemplates = 'RecordTemplates',
  RecordTemplateSettings = 'RecordTemplateSettings',
  RecordTemplateExpirationAutomations = 'RecordTemplateExpirationAutomations',
  RecordTemplatesForModule = 'RecordTemplatesForModule',
  RecordTemplatesUserHasAccessTo = 'RecordTemplatesUserHasAccessTo',
  RecordType = 'RecordType',
  RecordTypeApplicationGroups = 'RecordTypeApplicationGroups',
  RecordTypeFields = 'RecordTypeFields',
  AllFieldTemplatesInOrg = 'AllFieldTemplatesInOrg',
  RecordTypeForRecordTemplates = 'RecordTypeForRecordTemplates',
  RecordTypeIdForRecord = 'RecordTypeIdForRecord',
  RecordTypeMigrationState = 'RecordTypeMigrationState',
  RecordTypeOverview = 'RecordTypeOverview',
  RecordTypes = 'RecordTypes',
  RecordTypeSettings = 'RecordTypeSettings',
  RecordTypesOverview = 'RecordTypesOverview',
  RecordTypeStatuses = 'RecordTypeStatuses',
  RecordTypeViewDashboards = 'RecordTypeViewDashboards',
  RecordUpdatedRecordTemplateHistories = 'RecordUpdatedRecordTemplateHistories',
  RenewalPolicies = 'RenewalPolicies',
  ReportsList = 'ReportsList',
  ReportTypeOptions = 'ReportTypeOptions',
  ScrapeTemplate = 'ScrapeTemplate',
  ScrapeTemplates = 'ScrapeTemplates',
  SearchAIChatbotFAQs = 'SearchAIChatbotFAQs',
  SearchAIChats = 'SearchAIChats',
  SearchCompanies = 'SearchCompanies',
  SearchEntities = 'SearchEntities',
  SearchEntityVersions = 'SearchEntityVersions',
  SearchEntityVersionsByLocation = 'SearchEntityVersionsByLocation',
  SearchInspectionTemplates = 'SearchInspectionTemplates',
  SearchPlanSetFilePages = 'SearchPlanSetFilePages',
  SearchUsers = 'SearchUsers',
  SendRecordEmailModal = 'SendRecordEmailModal',
  SignatureByUserUuid = 'SignatureByUserUuid',
  StampTemplate = 'StampTemplate',
  StampTemplates = 'StampTemplates',
  StampTemplatesSelect = 'StampTemplatesSelect',
  TaskTemplates = 'TaskTemplates',
  UnclaimedImportedRecordSearch = 'UnclaimedImportedRecordSearch',
  UniversalSearch = 'UniversalSearch',
  User = 'User',
  UserPendingRecordClaims = 'UserPendingRecordClaims',
  Users = 'Users',
  UsersThatCanBeInputsForField = 'UsersThatCanBeInputsForField',
  UsersWithAccessToModule = 'UsersWithAccessToModule',
  Valuations = 'Valuations',
  ViewCustomColumnTemplates = 'ViewCustomColumnTemplates',
  ViewDashboardById = 'ViewDashboardById',
  ViewDashboardByUuid = 'ViewDashboardByUuid',
  Views = 'Views',
  ViewsData = 'ViewsData',
  ViewsDataCounts = 'ViewsDataCounts',
  WorkOrderTypes = 'WorkOrderTypes',
  ViewsDataCountsForViewDashboard = 'ViewsDataCountsForViewDashboard',
  ViewsDataForViewDashboard = 'ViewsDataForViewDashboard',
  ViewAttemptResultsModal = 'ViewAttemptResultsModal',
  ViewInspectionResultsModal = 'ViewInspectionResultsModal',
  ViolationInspection = 'ViolationInspection',
  WidgetData = 'WidgetData',
  Workflow = 'Workflow',
  WorkflowSection = 'WorkflowSection',
  WorkflowTemplate = 'WorkflowTemplate',
  WorkflowTemplates = 'WorkflowTemplates',
  WorkflowTemplatesTab = 'WorkflowTemplatesTab',
}

export const queryKeyFactories = {
  recordPage: Object.assign(
    (recordUuid: string) => {
      return [QueryKey.Record, recordUuid]
    },
    {
      activity(recordUuid: string) {
        return [...queryKeyFactories.recordPage(recordUuid), QueryKey.RecordActivityTab]
      },
      fees(recordUuid: string) {
        return [...queryKeyFactories.recordPage(recordUuid), QueryKey.RecordFeesTab]
      },
      inspections(recordUuid: string) {
        return [...queryKeyFactories.recordPage(recordUuid), QueryKey.RecordInspectionsTab]
      },
      properties(recordUuid: string) {
        return [...queryKeyFactories.recordPage(recordUuid), QueryKey.RecordPropertyTab]
      },
      workflow(recordUuid: string) {
        return [...queryKeyFactories.recordPage(recordUuid), QueryKey.WorkflowSection]
      },
    }
  ),
}

const useQueryClient = () => {
  // const { notification } = useNotification()
  return useMemo(
    () =>
      new _QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            const queryKey = query.queryKey[0] as QueryKey
            switch (queryKey) {
              default:
                monitoring.logError(error, { source: 'QueryCache', queryKey })
                break
            }
          },
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      }),
    []
  )
}

export const QueryClientProvider = ({ children }: { children?: React.ReactNode }) => {
  const queryClient = useQueryClient()

  return <ReactQueryClientProvider client={queryClient}>{children}</ReactQueryClientProvider>
}

type UseQueryArgs<
  TData,
  TVariables extends Record<string, unknown> | void = void,
  TError = DefaultError,
  TQueryKey extends ReactQueryKey = ReactQueryKey,
> = Omit<ReactUseQueryOptions<TData, TError, TData, TQueryKey>, 'queryFn'> &
  (
    | {
        queryDocument: TypedDocumentNode<TData, TVariables>
        queryFn?: never
      }
    | { queryFn: QueryFunction<TData, TQueryKey, never>; queryDocument?: never }
  ) &
  (TVariables extends void
    ? {
        variables?: never
      }
    : {
        variables: TVariables
      })

type GovWellQueryOptions = {
  enableCancellation?: boolean
}
export type UseQueryOptions<TData, TVariables extends Record<string, unknown> | void = void> = Omit<
  UseQueryArgs<TData, TVariables>,
  'queryKey' | 'queryFn' | 'queryDocument' | 'variables'
> &
  GovWellQueryOptions

/**
 * @description Query hook wrapper that allows both REST and GQL operations
 * @param options Configure one of `queryFn` for REST or (`queryDocument` and `variables`) for GQL
 */
export const useQuery = <
  TData,
  TVariables extends Record<string, unknown> | void = void,
  TError = DefaultError,
  TQueryKey extends ReactQueryKey = ReactQueryKey,
>({
  queryFn,
  queryDocument,
  variables,
  ...options
}: UseQueryArgs<TData, TVariables, TError, TQueryKey> & GovWellQueryOptions) => {
  const client = useApolloClient()
  return useReactQuery<TData, TError, TData, TQueryKey>({
    ...options,
    queryFn: queryFn
      ? queryFn
      : ({ signal }) =>
          client
            .query({
              query: queryDocument,
              variables,
              fetchPolicy: 'no-cache',
              context: {
                fetchOptions: {
                  ...(options.enableCancellation ? { signal } : {}),
                },
              },
            })
            .then((result) => result.data),
  })
}

export type UseMutationArgs<
  TData,
  TVariables extends Record<string, unknown> | void = void,
  TError = DefaultError,
  TMutationKey extends ReactMutationKey = ReactMutationKey,
> = Omit<ReactUseMutationOptions<TData, TError, TVariables, TMutationKey>, 'mutationFn'> &
  (
    | {
        mutationDocument: TypedDocumentNode<TData, TVariables>
        mutationFn?: never
      }
    | {
        mutationFn: MutationFunction<TData, TVariables>
        mutationDocument?: never
      }
  )

export type UseMutationOptions<
  TData,
  TVariables extends Record<string, unknown> | void = void,
> = Omit<UseMutationArgs<TData, TVariables>, 'mutationDocument' | 'mutationFn'>

export const useMutation = <
  TData,
  TVariables extends Record<string, unknown> | undefined = undefined,
  TError = DefaultError,
  TMutationKey extends ReactMutationKey = ReactMutationKey,
>({
  mutationFn,
  mutationDocument,
  ...options
}: UseMutationArgs<TData, TVariables, TError, TMutationKey>) => {
  const client = useApolloClient()
  const { notification } = useNotification()
  return useReactMutation<TData, TError, TVariables, TMutationKey>({
    onError: (e) => {
      monitoring.logError(e, { source: 'useMutation' })
      if (e instanceof Error) {
        notification.error({
          message: 'Something went wrong',
          description: e.message,
        })
      } else {
        notification.error({
          message: 'Something went wrong',
          description:
            'An error occurred. Please try again or contact support by clicking the orange chat icon at the bottom of the screen.',
        })
      }
    },
    ...options,
    mutationFn: mutationFn
      ? mutationFn
      : (variables: TVariables) =>
          client
            .mutate({
              mutation: mutationDocument,
              variables,
              fetchPolicy: 'no-cache',
            })
            .then((result) => {
              if (!result.data) {
                throw new Error('Mutation data unavailable')
              }
              return result.data
            }),
  })
}
